// Gap identity + patch helpers for pinned day travel estimates
// (trip_days.travel_estimates, migration 231). A gap is identified by BOTH
// endpoints ("fromStop|toStop") so a reorder changes the key and the stale
// time simply stops rendering, instead of mislabeling the new pair.

export const activityGapId = (activity) => {
  const id = activity?.activityId ?? activity?.id;
  return id != null && id !== '' ? `a:${id}` : null;
};

export const accommodationGapId = (acc) =>
  acc ? `acc:${acc.clientUid || acc.id || 'stay'}` : null;

// Home anchor identity (day-1 morning chips). Embeds the user id — on a
// shared trip each traveler's home is their own, so two people's home
// estimates must never collide on one key. The server strips home:-anchored
// estimates for everyone but trip members.
export const homeGapId = (userId) => (userId ? `home:${userId}` : null);

// 0.01° cell snap (~1.1 km) — the drive cache's own grain (pairKey in
// driveTimeService). Applied to HOME coordinates before they leave the
// client, so the precise point never reaches the request, the router, or
// any cache row; a stable snap also defeats averaging across repeats.
export const snapToCell = (coords) => {
  const lat = Number(coords?.lat);
  const lng = Number(coords?.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat: Math.round(lat * 100) / 100, lng: Math.round(lng * 100) / 100 };
};

export const gapKey = (fromId, toId) => (fromId && toId ? `${fromId}|${toId}` : null);

// Activities carry coordinates in two shapes (flat lat/lng for place-sourced,
// nested coordinates{} for custom-sourced). Null-checked BEFORE Number() —
// Number(null) is 0, which is a real place in the Gulf of Guinea.
export const getActivityCoords = (activity) => {
  const rawLat = activity?.lat ?? activity?.coordinates?.lat;
  const rawLng = activity?.lng ?? activity?.coordinates?.lng;
  if (rawLat == null || rawLng == null) return null;
  const lat = Number(rawLat);
  const lng = Number(rawLng);
  return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
};

/**
 * Next travelEstimates map after saving (estimate object) or clearing (null)
 * one gap. Prunes keys that no longer match a rendered gap while it's here —
 * reorders and removals leave orphans behind by design (they stop rendering
 * immediately; this is where they're actually dropped).
 */
export function buildTravelEstimatesNext(day, validKeys, key, estimate) {
  const current = day?.travelEstimates || {};
  const next = {};
  for (const [k, v] of Object.entries(current)) {
    if (k !== key && validKeys.has(k)) next[k] = v;
  }
  if (estimate) next[key] = estimate;
  return next;
}

/**
 * Batch variant for "estimate the whole day": existing pins survive (pruned
 * to currently-rendered gaps, same as the single-save path), new entries
 * overlay them in ONE map so the caller makes ONE save — N sequential
 * single-saves would each rebuild from the same stale `day` and clobber
 * each other.
 */
export function buildTravelEstimatesBatch(day, validKeys, entries) {
  const current = day?.travelEstimates || {};
  const next = {};
  for (const [k, v] of Object.entries(current)) {
    if (validKeys.has(k)) next[k] = v;
  }
  for (const [k, v] of Object.entries(entries || {})) {
    if (v) next[k] = v;
  }
  return next;
}
